import { Chip } from "./Chip";
import { ChipRowFrame } from "./ChipRowFrame";
import type { UiTheme } from "../theme";

// One single-choice row of chips under its title: «Time (min)» above
// «[30] [45] [60] [90]». Born with its two rows in the «today» draft header
// (time and intensity, P0 57d74690), so the radiogroup a11y lives in one
// place. The title sits above, never beside: at 360 px a trailing word wrapped
// onto a line of its own.

export type ChipChoiceRowProps<T extends string | number> = {
  theme: UiTheme;
  choices: readonly T[];
  /** null: no chip is selected. */
  selected: T | null;
  onSelect: (value: T) => void;
  /** The chip's visible text. */
  label: (value: T) => string;
  /** What a screen reader says for one chip, unit included. */
  chipA11yLabel: (value: T) => string;
  /** The radiogroup's name. */
  groupA11yLabel: string;
  /** The heading above the chips, shown only: the radiogroup's name says it.
   *  Absent: no heading (the editor's day selector), `groupA11yLabel` names it. */
  title?: string;
  /** One line that scrolls sideways instead of wrapping: the day and round
   *  selectors of the program editor (D1..D7, W1..W12) on a 360 pt phone.
   *  A selection made off screen (the heatmap's jump) scrolls its chip in. */
  scroll?: boolean;
  /** Scrolling row only: side padding inside the scroller, so the first chip
   *  sits on the page gutter while the scroll still reaches the screen edges. */
  edgeInset?: number;
};

export function ChipChoiceRow<T extends string | number>({
  theme,
  choices,
  selected,
  onSelect,
  label,
  chipA11yLabel,
  groupA11yLabel,
  title,
  scroll = false,
  edgeInset = 0,
}: ChipChoiceRowProps<T>) {
  return (
    <ChipRowFrame
      theme={theme}
      choices={choices}
      groupRole="radiogroup"
      groupA11yLabel={groupA11yLabel}
      title={title}
      scroll={scroll}
      edgeInset={edgeInset}
      follow={selected}
      renderChip={(value) => (
        <Chip
          theme={theme}
          role="radio"
          prominent
          // Vertical only: horizontal slop would overlap the next chip.
          hitSlop={{ top: 8, bottom: 8 }}
          label={label(value)}
          accessibilityLabel={chipA11yLabel(value)}
          selected={selected === value}
          onPress={() => onSelect(value)}
        />
      )}
    />
  );
}
